import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { createRouteConfig } from '../utils/routeGuard';
import { Link } from '@tanstack/react-router';
import {
  SignedIn,
  SignedOut,
  SignInButton,
  SignUpButton,
  useAuth,
} from '@clerk/clerk-react';
import { Footer } from '../components/Footer';
import { useTranslation } from 'react-i18next';
import { staticI18n } from '../i18n';
import { useEffect } from 'react';
import { captureUtmParams } from '../utils/utmTracking';
import { BLOG_URL } from '../constants/urls';

// Pre-computed SEO translations to ensure they're available during SSR
const seoTitle = staticI18n.t('about.seo.title');
const seoDescription = staticI18n.t('about.seo.description');
const seoOgTitle = staticI18n.t('about.seo.ogTitle');
const seoOgDescription = staticI18n.t('about.seo.ogDescription');

export const Route = createFileRoute('/about')({
  component: About,
  // Add meta tags for SEO
  ...createRouteConfig({
    isPublic: true,
    seo: {
      title: seoTitle,
      description: seoDescription,
      ogTitle: seoOgTitle,
      ogDescription: seoOgDescription,
    },
  }),
});

function About() {
  const navigate = useNavigate();
  const { isSignedIn, isLoaded } = useAuth();
  const { t } = useTranslation();
  const showBlogLink = import.meta.env.VITE_SHOW_BLOG_LINK === 'true';

  // Capture UTM parameters from URL
  useEffect(() => {
    captureUtmParams();
  }, []);

  // Redirect to dashboard if user is already logged in
  useEffect(() => {
    if (isLoaded && isSignedIn) {
      navigate({ to: '/dashboard' });
    }
  }, [isLoaded, isSignedIn, navigate]);

  return (
    <div className='min-h-screen flex flex-col items-center px-4 sm:px-6 lg:px-8 py-12'>
      {/* Main Content */}
      <div className='w-full max-w-4xl mx-auto mb-4'>
        {/* Header */}
        <div className='text-center mb-12'>
          <h1 className='text-3xl sm:text-4xl font-bold mb-4'>
            {t('about.title')}
          </h1>
          <div className='w-24 h-1 bg-blue-600 mx-auto mb-6'></div>
          <p className='text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto'>
            {t('about.subtitle')}
          </p>

          <SignedOut>
            <div className='mt-4'>
              <p className='text-sm text-gray-600 dark:text-gray-400'>
                {t('common.existingCustomer')}{' '}
                <SignInButton mode='modal'>
                  <button className='text-blue-600 hover:underline font-medium'>
                    {t('common.logIn')}
                  </button>
                </SignInButton>
              </p>
            </div>
          </SignedOut>
        </div>

        {/* Blog Link Section */}
        {showBlogLink && (
          <section className='mb-16 text-center'>
            <div className='bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl p-8'>
              <h2 className='text-xl font-bold mb-2'>
                {t('about.sections.blog.heading')}
              </h2>
              <p className='text-gray-600 dark:text-gray-300 mb-6'>
                {t('about.sections.blog.description')}
              </p>
              <a
                href={BLOG_URL}
                target='_blank'
                rel='noopener noreferrer'
                aria-label='Read our blog (opens in a new tab)'
                className='px-8 py-3 rounded-md bg-blue-600 text-white font-medium hover:bg-blue-700 transition-all inline-flex items-center justify-center gap-2'
              >
                {t('about.sections.blog.cta')}
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  className='h-4 w-4'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                  aria-hidden='true'
                  focusable='false'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14'
                  />
                </svg>
              </a>
            </div>
          </section>
        )}

        {/* Our Story */}
        <section className='mb-16'>
          <h2 className='text-2xl font-bold mb-6'>
            {t('about.sections.story.title')}
          </h2>
          <div className='prose prose-lg dark:prose-invert max-w-none'>
            {(
              t('about.sections.story.paragraphs', {
                returnObjects: true,
              }) as string[]
            ).map((paragraph: string, index: number) => (
              <p key={index}>{paragraph}</p>
            ))}
          </div>
        </section>

        {/* How It Works */}
        <section className='mb-16'>
          <h2 className='text-2xl font-bold mb-6'>
            {t('about.sections.howItWorks.title')}
          </h2>
          <div className='space-y-8'>
            {(
              t('about.sections.howItWorks.steps', {
                returnObjects: true,
              }) as Array<{
                number: string;
                title: string;
                description: string;
              }>
            ).map(
              (
                step: { number: string; title: string; description: string },
                index: number
              ) => (
                <div key={index} className='flex flex-col md:flex-row gap-6'>
                  <div className='md:w-16 flex-shrink-0 flex justify-center'>
                    <div className='w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-blue-600 text-xl font-bold'>
                      {step.number}
                    </div>
                  </div>
                  <div>
                    <h3 className='text-xl font-semibold mb-2'>{step.title}</h3>
                    <p className='text-gray-600 dark:text-gray-300'>
                      {step.description}
                    </p>
                  </div>
                </div>
              )
            )}
          </div>
        </section>

        {/* Pricing */}
        <section className='mb-16 text-center'>
          <h2 className='text-2xl font-bold mb-4'>
            {t('about.sections.pricing.title')}
          </h2>
          <p className='text-lg text-gray-600 dark:text-gray-300 mb-8 max-w-2xl mx-auto'>
            {t('about.sections.pricing.subtitle')}
          </p>
          <div className='grid grid-cols-1 md:grid-cols-3 gap-8'>
            {/* Tier 1 */}
            <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md p-8'>
              <h3 className='text-xl font-bold mb-2'>
                {t('about.sections.pricing.tiers.tier1.title')}
              </h3>
              <div className='text-3xl font-bold mb-4'>
                <span>{t('about.sections.pricing.tiers.tier1.price')}</span>
                <span className='text-sm text-gray-500 dark:text-gray-400 ml-1'>
                  {t('about.sections.pricing.tiers.tier1.period')}
                </span>
              </div>
              <p className='text-gray-600 dark:text-gray-300 mb-6'>
                {t('about.sections.pricing.tiers.tier1.description')}
                <span className='block mt-2 text-blue-500 font-medium'>
                  {t('about.sections.pricing.tiers.tier1.trialNote')}
                </span>
              </p>
              <ul className='space-y-3 mb-8 text-left text-sm'>
                {(
                  t('about.sections.pricing.tiers.tier1.features', {
                    returnObjects: true,
                  }) as string[]
                ).map((feature: string, index: number) => (
                  <li key={index} className='flex items-center'>
                    <svg
                      className='h-5 w-5 text-green-500 mr-2 flex-shrink-0'
                      fill='none'
                      viewBox='0 0 24 24'
                      stroke='currentColor'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth='2'
                        d='M5 13l4 4L19 7'
                      />
                    </svg>
                    <span>{feature} </span>
                  </li>
                ))}
              </ul>
              <div className='text-center mt-auto'>
                <SignedIn>
                  <Link
                    to='/dashboard'
                    className='px-6 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-all inline-block w-full'
                  >
                    {t('common.selectPlan')}
                  </Link>
                </SignedIn>
                <SignedOut>
                  <SignUpButton mode='modal'>
                    <button className='px-6 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-all w-full inline-flex items-center justify-center'>
                      {t('common.tryFeaturesForFree')}
                    </button>
                  </SignUpButton>
                </SignedOut>
              </div>
            </div>

            {/* Tier 2 */}
            <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md p-8 border-2 border-blue-500 relative'>
              <div className='absolute top-0 right-0 left-0 bg-blue-500 text-white text-xs font-bold py-1'>
                {t('about.sections.pricing.tiers.tier2.badge')}
              </div>
              <h3 className='text-xl font-bold mb-2 mt-4'>
                {t('about.sections.pricing.tiers.tier2.title')}
              </h3>
              <div className='text-3xl font-bold mb-4'>
                <span>{t('about.sections.pricing.tiers.tier2.price')}</span>
                <span className='text-sm text-gray-500 dark:text-gray-400 ml-1'>
                  {t('about.sections.pricing.tiers.tier2.period')}
                </span>
              </div>
              <p className='text-gray-600 dark:text-gray-300 mb-6'>
                {t('about.sections.pricing.tiers.tier2.description')}
                <span className='block mt-2 text-blue-500 font-medium'>
                  {t('about.sections.pricing.tiers.tier2.trialNote')}
                </span>
              </p>
              <ul className='space-y-3 mb-8 text-left text-sm'>
                {(
                  t('about.sections.pricing.tiers.tier2.features', {
                    returnObjects: true,
                  }) as string[]
                ).map((feature: string, index: number) => (
                  <li key={index} className='flex items-center'>
                    <svg
                      className='h-5 w-5 text-green-500 mr-2 flex-shrink-0'
                      fill='none'
                      viewBox='0 0 24 24'
                      stroke='currentColor'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth='2'
                        d='M5 13l4 4L19 7'
                      />
                    </svg>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <div className='text-center mt-auto'>
                <SignedIn>
                  <Link
                    to='/dashboard'
                    className='px-6 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-all inline-block w-full'
                  >
                    {t('common.selectPlan')}
                  </Link>
                </SignedIn>
                <SignedOut>
                  <SignUpButton mode='modal'>
                    <button className='px-6 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-all w-full inline-flex items-center justify-center'>
                      {t('common.tryFeaturesForFree')}
                    </button>
                  </SignUpButton>
                </SignedOut>
              </div>
            </div>

            {/* Tier 3 */}
            <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md p-8'>
              <h3 className='text-xl font-bold mb-2'>
                {t('about.sections.pricing.tiers.tier3.title')}
              </h3>
              <div className='text-3xl font-bold mb-4'>
                <span>{t('about.sections.pricing.tiers.tier3.price')}</span>
                <span className='text-sm text-gray-500 dark:text-gray-400 ml-1'>
                  {t('about.sections.pricing.tiers.tier3.period')}
                </span>
              </div>
              <p className='text-gray-600 dark:text-gray-300 mb-6'>
                {t('about.sections.pricing.tiers.tier3.description')}
                <span className='block mt-2 text-blue-500 font-medium'>
                  {t('about.sections.pricing.tiers.tier3.trialNote')}
                </span>
              </p>
              <ul className='space-y-3 mb-8 text-left text-sm'>
                {(
                  t('about.sections.pricing.tiers.tier3.features', {
                    returnObjects: true,
                  }) as string[]
                ).map((feature: string, index: number) => (
                  <li key={index} className='flex items-center'>
                    <svg
                      className='h-5 w-5 text-green-500 mr-2 flex-shrink-0'
                      fill='none'
                      viewBox='0 0 24 24'
                      stroke='currentColor'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth='2'
                        d='M5 13l4 4L19 7'
                      />
                    </svg>
                    <span>
                      {feature}
                      {feature.includes('Duplicate') && (
                        <span className='text-blue-500 font-medium'>
                          {' '}
                          ({t('common.comingSoon')})
                        </span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
              <div className='text-center mt-auto'>
                <SignedIn>
                  <Link
                    to='/dashboard'
                    className='px-6 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-all inline-block w-full'
                  >
                    {t('common.selectPlan')}
                  </Link>
                </SignedIn>
                <SignedOut>
                  <SignUpButton mode='modal'>
                    <button className='px-6 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-all w-full inline-flex items-center justify-center'>
                      {t('common.tryFeaturesForFree')}
                    </button>
                  </SignUpButton>
                </SignedOut>
              </div>
            </div>
          </div>

          {/* Future Features Roadmap */}
          <div className='mt-16 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-xl p-8 max-w-4xl mx-auto'>
            <h3 className='text-2xl font-bold mb-4 text-gradient bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600'>
              {t('about.sections.future.title')}
            </h3>
            <p className='text-gray-700 dark:text-gray-300 mb-6'>
              {t('about.sections.future.subtitle')}
            </p>

            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              {(
                t('about.sections.future.features', {
                  returnObjects: true,
                }) as Array<{ title: string; description: string }>
              ).map(
                (
                  feature: { title: string; description: string },
                  index: number
                ) => {
                  // Generate different colors based on index
                  const colors = [
                    {
                      bg: 'bg-blue-100 dark:bg-blue-900',
                      text: 'text-blue-600',
                      icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
                    },
                    {
                      bg: 'bg-purple-100 dark:bg-purple-900',
                      text: 'text-purple-600',
                      icon: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
                    },
                    {
                      bg: 'bg-green-100 dark:bg-green-900',
                      text: 'text-green-600',
                      icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
                    },
                    {
                      bg: 'bg-red-100 dark:bg-red-900',
                      text: 'text-red-600',
                      icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
                    },
                  ];
                  const color = colors[index % colors.length];

                  return (
                    <div
                      key={index}
                      className='bg-white dark:bg-gray-800 rounded-lg p-5 shadow-sm'
                    >
                      <div className='flex items-center mb-3'>
                        <div
                          className={`w-10 h-10 rounded-full ${color.bg} flex items-center justify-center mr-3`}
                        >
                          <svg
                            xmlns='http://www.w3.org/2000/svg'
                            className={`h-6 w-6 ${color.text}`}
                            fill='none'
                            viewBox='0 0 24 24'
                            stroke='currentColor'
                          >
                            <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              strokeWidth={2}
                              d={color.icon}
                            />
                          </svg>
                        </div>
                        <h4 className='text-lg font-semibold'>
                          {feature.title}
                        </h4>
                      </div>
                      <p className='text-gray-600 dark:text-gray-300 pl-13'>
                        {feature.description}
                      </p>
                    </div>
                  );
                }
              )}
            </div>

            <div className='text-center mt-8'>
              <span className='inline-block px-4 py-2 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-sm font-medium'>
                {t('about.sections.future.note')}
              </span>
            </div>
            <p className='text-center mt-3 text-sm text-gray-500 dark:text-gray-400 italic'>
              {t('about.sections.future.disclaimer')}
            </p>
          </div>
        </section>

        {/* Join Us CTA */}
        <section className='text-center'>
          <h2 className='text-2xl font-bold mb-4'>
            {t('about.sections.cta.title')}
          </h2>
          <p className='text-lg text-gray-600 dark:text-gray-300 mb-6 max-w-2xl mx-auto'>
            {t('about.sections.cta.description')}
          </p>
          <Link
            to='/'
            className='px-8 py-3 rounded-md bg-blue-600 text-white font-medium hover:bg-blue-700 transition-all inline-block'
          >
            {t('common.backToHome')}
          </Link>
        </section>
      </div>

      <Footer />
    </div>
  );
}
